/*
 * dondefue.js — orquestador front-end del tema.
 * Carga al final del body, defer.
 *
 * Responsabilidades:
 *  - Entrada animada (.df-enter) con IntersectionObserver + stagger.
 *  - Sticky header: scroll state + is-scrolled / is-over-red.
 *  - Search modal: open/close + bind a [data-toggle-search].
 *  - Hooks generales (utilidades) reutilizables.
 */
(function () {
  'use strict';

  // ============================================================
  // Util
  // ============================================================
  var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function on(el, ev, fn, opts) {
    if (!el) return;
    el.addEventListener(ev, fn, opts || false);
  }

  // ============================================================
  // Auto-tag v3 cards con .df-enter para que reciban la animación
  // ============================================================
  function autoTagEnter() {
    var selectors = [
      '.df-bs-card',
      '.df-pp-card',
      '.df-occ-card',
      '.df-cat-card',
      '.df-pack-card',
      '.df-demo-step',
      '.df-est-card',
      '.df-lang-card',
      '.df-tool-card',
      '.df-diario-card',
      '.df-hero-red__product-v3',
      '.df-trust-bar__item',
      '.df-vshow__card',
      '.df-ugcw__tile',
      '.df-vtest__card',
      '.df-reels__slide'
    ];
    try {
      document.querySelectorAll(selectors.join(',')).forEach(function (el) {
        el.classList.add('df-enter');
      });
    } catch (e) {}
  }

  // ============================================================
  // Enter animation
  //
  // enterIO is module-scoped (not a local of initEnterObserver) so that
  // content appended after boot — collection "Ver más", search & blog
  // load-more — can be re-observed via window.dfAutoTagEnter. Without
  // that, freshly inserted .df-enter cards were never observed and stayed
  // stuck at opacity:0 forever (the "Ver más no funciona" bug).
  // ============================================================
  var enterIO = null;

  function observeEnter() {
    // No IntersectionObserver (or reduced motion → enterIO never built):
    // reveal immediately so cards are never left invisible.
    if (!enterIO) {
      document.querySelectorAll('.df-enter:not(.is-in)').forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    // observe() on an already-observed node is a no-op, so re-scanning the
    // whole document only ever picks up the newly inserted cards.
    document.querySelectorAll('.df-enter:not(.is-in)').forEach(function (el) { enterIO.observe(el); });
  }

  function initEnterObserver() {
    if (prefersReduced || !('IntersectionObserver' in window)) {
      observeEnter();
      return;
    }
    enterIO = new IntersectionObserver(function (entries) {
      // Group entries by parent for stagger
      var byParent = new Map();
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var p = entry.target.parentElement;
        if (!byParent.has(p)) byParent.set(p, []);
        byParent.get(p).push(entry.target);
      });
      byParent.forEach(function (els) {
        els.forEach(function (el, i) {
          setTimeout(function () { el.classList.add('is-in'); }, i * 60);
        });
        els.forEach(function (el) { enterIO.unobserve(el); });
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });

    observeEnter();
  }

  // Public hook for AJAX-appended content (load-more, filters, search):
  // tag any new v3 cards, then observe/reveal the freshly inserted ones.
  window.dfAutoTagEnter = function () {
    autoTagEnter();
    observeEnter();
  };

  // ============================================================
  // Sticky header — backup observer.
  // (df-header section ya añade is-scrolled via window scroll; este
  //  observer detecta secciones bajo el header con clase df-section--red
  //  o data-df-over-red para alternar is-over-red de forma reactiva.)
  // ============================================================
  function initHeaderObserver() {
    var header = document.querySelector('[data-df-header]');
    if (!header) return;
    if (!('IntersectionObserver' in window)) return;

    var redSections = document.querySelectorAll('main .df-section--red, main [data-df-over-red="true"]');
    if (!redSections.length) return;

    var io = new IntersectionObserver(function (entries) {
      // Si alguna sección roja está intersectando con la zona del header, activa is-over-red
      var anyOver = false;
      entries.forEach(function (entry) {
        if (entry.isIntersecting) anyOver = true;
      });
      header.classList.toggle('is-over-red', anyOver);
    }, {
      // El root margin top:-headerHeight ya hace que solo intersecte cuando la sección está bajo el header
      rootMargin: '-' + (header.offsetHeight || 80) + 'px 0px -90% 0px',
      threshold: 0
    });
    redSections.forEach(function (s) { io.observe(s); });
  }

  // ============================================================
  // Search modal
  // ============================================================
  function initSearchModal() {
    var modal = document.getElementById('df-search-modal');
    if (!modal) return;

    var triggers = document.querySelectorAll('[data-toggle-search]');
    var closers = modal.querySelectorAll('[data-df-search-close]');
    var input = modal.querySelector('input[type="search"]');

    var opener = null;
    var hideTimer = null;
    function openModal() {
      if (document.documentElement.dataset.searchOpen === 'true') return;
      opener = document.activeElement;
      clearTimeout(hideTimer);
      modal.hidden = false;
      requestAnimationFrame(function () {
        modal.classList.add('is-open');
        if (input) input.focus();
      });
      document.body.style.overflow = 'hidden';
      document.documentElement.dataset.searchOpen = 'true';
    }
    function closeModal() {
      modal.classList.remove('is-open');
      hideTimer = setTimeout(function () { modal.hidden = true; }, 240);
      document.body.style.overflow = '';
      delete document.documentElement.dataset.searchOpen;
      // El foco vuelve a la lupa (o a lo que lo abrió); antes caía en <body>.
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
      opener = null;
    }

    triggers.forEach(function (t) {
      on(t, 'click', function (e) { e.preventDefault(); openModal(); });
    });
    closers.forEach(function (c) {
      on(c, 'click', closeModal);
    });

    // ESC closes
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && document.documentElement.dataset.searchOpen === 'true') {
        closeModal();
      }
    });

    // ⌘K / Ctrl+K opens
    on(document, 'keydown', function (e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (document.documentElement.dataset.searchOpen === 'true') closeModal();
        else openModal();
      }
    });

    // Click outside panel closes
    on(modal, 'click', function (e) {
      if (e.target === modal || e.target.classList.contains('df-search-modal__backdrop')) closeModal();
    });
  }

  // ============================================================
  // Cart count: actualiza [data-cart-count] cuando el cart drawer del fork se entera de un add
  // ============================================================
  function initCartCountObserver() {
    if (!window.cartStrings && !document.querySelector('cart-service')) return;
    var countEls = document.querySelectorAll('[data-cart-count]');
    if (!countEls.length) return;

    document.addEventListener('cart:updated', function (e) {
      if (e.detail && typeof e.detail.itemCount === 'number') {
        countEls.forEach(function (el) { el.textContent = String(e.detail.itemCount); });
      }
    });

    // Backup: cuando el form de PDP completa el add, el fork añade clase .successful al botón.
    // Hacemos un fetch ligero al cart.js para actualizar el counter.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[type="submit"]');
      if (!btn || !btn.closest('form[action="/cart/add"]')) return;
      setTimeout(function () {
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            countEls.forEach(function (el) { el.textContent = String(cart.item_count); });
          })
          .catch(function () {});
      }, 700);
    });
  }

  // ============================================================
  // Smooth scroll para anchors internos df-magindex
  // (Ya está en df-magazine-index.liquid, pero por si hay anchors manuales)
  // ============================================================
  function initSmoothAnchors() {
    document.querySelectorAll('a[href^="#"]:not([data-df-magindex-anchor]):not([href="#"])').forEach(function (a) {
      a.addEventListener('click', function (e) {
        var href = a.getAttribute('href');
        if (!href || href === '#') return;
        var target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
        }
      });
    });
  }

  // ============================================================
  // Wishlist (localStorage based)
  // ============================================================
  var WISHLIST_KEY = 'df_wishlist';

  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    } catch (e) { return []; }
  }
  function saveWishlist(arr) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(arr)); } catch (e) {}
    document.dispatchEvent(new CustomEvent('df:wishlist:change', { detail: { items: arr } }));
  }
  function toggleWishlist(handle) {
    var list = getWishlist();
    var i = list.indexOf(handle);
    if (i === -1) list.push(handle);
    else list.splice(i, 1);
    saveWishlist(list);
    return list.indexOf(handle) !== -1;
  }
  function isInWishlist(handle) {
    return getWishlist().indexOf(handle) !== -1;
  }

  function syncWishlistButtons() {
    var list = getWishlist();
    document.querySelectorAll('[data-df-wishlist-toggle]').forEach(function (btn) {
      var h = btn.getAttribute('data-df-wishlist-toggle');
      btn.classList.toggle('is-active', list.indexOf(h) !== -1);
      btn.setAttribute('aria-pressed', String(list.indexOf(h) !== -1));
    });
    var countEls = document.querySelectorAll('[data-df-wishlist-count]');
    countEls.forEach(function (el) {
      el.textContent = String(list.length);
      el.classList.toggle('is-empty', list.length === 0);
    });
  }

  function initWishlist() {
    syncWishlistButtons();
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-df-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      var handle = btn.getAttribute('data-df-wishlist-toggle');
      var nowIn = toggleWishlist(handle);
      btn.classList.toggle('is-active', nowIn);
      btn.setAttribute('aria-pressed', String(nowIn));
      // Animación feedback
      btn.classList.add('is-pulse');
      setTimeout(function () { btn.classList.remove('is-pulse'); }, 360);
    });
    document.addEventListener('df:wishlist:change', syncWishlistButtons);

    // Wishlist page rendering — if container exists, render items
    var container = document.querySelector('[data-df-wishlist-list]');
    if (container) renderWishlistPage(container);
  }

  function renderWishlistPage(container) {
    var list = getWishlist();
    var emptyState = document.querySelector('[data-df-wishlist-empty]');
    if (!list.length) {
      container.innerHTML = '';
      if (emptyState) emptyState.style.display = '';
      return;
    }
    if (emptyState) emptyState.style.display = 'none';

    Promise.all(list.map(function (h) {
      return fetch('/products/' + h + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      container.innerHTML = '';
      products.filter(Boolean).forEach(function (p) {
        var card = document.createElement('article');
        card.className = 'df-product-card df-card df-card--lg';
        card.innerHTML =
          '<a href="' + p.url + '" class="df-product-card__media df-cover df-aspect-1" style="display:block;position:relative;border-bottom:var(--df-border)">' +
            (p.featured_image ? '<img src="' + p.featured_image + '" alt="' + p.title + '" loading="lazy" width="600" height="600">' : '') +
            '<button type="button" data-df-wishlist-toggle="' + p.handle + '" class="df-product-card__plus df-wishlist-btn is-active" aria-label="Quitar de favoritos" style="position:absolute;top:14px;right:14px;width:36px;height:36px;border-radius:50%;background:var(--df-color-red);color:#fff;border:none;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;z-index:2">' +
              '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 10c0 5.5-7 10-7 10Z"/></svg>' +
            '</button>' +
          '</a>' +
          '<div class="df-product-card__body" style="padding:14px 16px;border-top:var(--df-border);background:#fff">' +
            '<h3 class="df-h df-h--19" style="margin:0"><a href="' + p.url + '" style="color:inherit;text-decoration:none">' + p.title + '</a></h3>' +
            '<div style="display:flex;justify-content:space-between;align-items:center;margin-top:10px"><span style="font-family:var(--df-font-sans);font-size:15px;font-weight:700;color:var(--df-color-red)">' + (p.price / 100).toFixed(2).replace('.', ',') + ' €</span></div>' +
          '</div>';
        container.appendChild(card);
      });
      syncWishlistButtons();
    });
  }

  // ============================================================
  // Exit-intent popup
  // ============================================================
  function initExitPopup() {
    var popup = document.getElementById('df-exit-popup');
    if (!popup) return;
    if (localStorage.getItem('df_exit_seen') === '1') return;

    var settings = popup.dataset;
    var minDelay = Number(document.querySelector('[data-df-popup-delay]') ? document.querySelector('[data-df-popup-delay]').dataset.delay : 15) * 1000;
    var armed = false;
    setTimeout(function () { armed = true; }, minDelay);

    function open() {
      if (popup.hidden === false) return;
      popup.hidden = false;
      requestAnimationFrame(function () { popup.classList.add('is-open'); });
      localStorage.setItem('df_exit_seen', '1');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      popup.classList.remove('is-open');
      setTimeout(function () { popup.hidden = true; }, 300);
      document.body.style.overflow = '';
    }

    // Desktop: mouseleave hacia arriba del viewport
    if (window.matchMedia('(min-width: 768px)').matches) {
      document.addEventListener('mouseleave', function (e) {
        if (!armed) return;
        if (e.clientY < 8) open();
      });
    } else {
      // Mobile: scroll up rápido tras scroll down (proxy de "se va")
      var lastY = window.scrollY, lastT = Date.now(), maxScroll = 0;
      window.addEventListener('scroll', function () {
        if (!armed) return;
        var y = window.scrollY, t = Date.now();
        maxScroll = Math.max(maxScroll, y);
        var dy = y - lastY, dt = t - lastT;
        if (dt > 0 && dy < -80 && Math.abs(dy / dt) > 1.5 && maxScroll > 600) {
          open();
        }
        lastY = y; lastT = t;
      }, { passive: true });
    }

    // Fallback: 45s en página
    setTimeout(function () { if (armed) open(); }, Math.max(minDelay, 45000));

    // Cerrar
    popup.querySelectorAll('[data-df-exit-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !popup.hidden) close();
    });
  }

  // ============================================================
  // Boot
  // ============================================================
  function boot() {
    autoTagEnter();
    initEnterObserver();
    initHeaderObserver();
    initSearchModal();
    initCartCountObserver();
    initSmoothAnchors();
    initExitPopup();
    initWishlist();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
